import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LifeBuoy } from 'lucide-react';
import { TELAS_EMERGENCIA, ativarModoEmergencia, useEmergencia, useSemConexao, voltarAoNormal } from '../lib/emergencia';
import { enviarPendentes } from '../lib/emergencia-copia';
import { useAuth } from '../lib/auth';
import { useAlertModal } from './DialogProvider';

/**
 * Sobe os orcamentos da emergencia quando o servidor responde e ha alguem logado — ao voltar da
 * queda e tambem em qualquer abertura (o computador pode ter sido desligado com pendentes).
 * O resultado fica na tela ate alguem fechar: "subiu" e "nao subiu" nao podem sumir num toast.
 */
export function EnvioDaEmergencia() {
  const semConexao = useSemConexao();
  // Com o modo ligado as chamadas nem saem: o envio espera o "Voltar ao normal" ([DA-849]).
  const emergencia = useEmergencia();
  const { usuario } = useAuth();
  const { alerta } = useAlertModal();
  useEffect(() => {
    if (semConexao || emergencia || !usuario) return;
    void enviarPendentes().then(({ enviados, falhas }) => {
      if (!enviados && !falhas) return;
      const partes = [];
      if (enviados) partes.push(`${enviados} orcamento(s) feito(s) no modo emergencia subiram para o sistema e estao na lista de Orcamentos.`);
      if (falhas) partes.push(`${falhas} nao subiram — ficam guardados neste computador e o sistema tenta de novo na proxima abertura. Avise o Ruan.`);
      void alerta(partes.join(' '), { titulo: 'Modo emergencia', tipo: falhas ? 'erro' : undefined });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [semConexao, emergencia, usuario?.id]);
  return null;
}

// Tarja do MODO EMERGENCIA (lib/emergencia.ts). Laranja + preto = o par de ATENCAO da casa
// (daltonismo: nunca verde/vermelho), fixo e nao do tema — a tarja tem que ler igual em todos.
const COR = { background: '#f97316', color: '#000' } as const;
// Botao da tarja: preenchido (unica porta da acao) e com o par de selo da casa.
const BOTAO = { background: '#000', color: '#fde68a', border: 'none', borderRadius: 4, padding: '2px 10px', fontWeight: 700, fontSize: 13, cursor: 'pointer' } as const;

export function EmergenciaTarja() {
  const emergencia = useEmergencia();
  const semConexao = useSemConexao();
  if (!emergencia && !semConexao) return null;
  // Sem conexao, o modo NAO liga sozinho ([DA-849]): a tarja avisa e o usuario decide no botao.
  // Nada fica bloqueado enquanto ele nao clica; com o servidor de volta a tarja some.
  if (!emergencia) {
    return (
      <div role="alert" style={{ ...COR, padding: '3px 16px', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap', overflow: 'hidden' }}>
        <LifeBuoy size={16} />
        <span>SEM CONEXAO — o sistema nao esta respondendo.</span>
        <button
          type="button"
          onClick={ativarModoEmergencia}
          title={`Funcionam: ${TELAS_EMERGENCIA.map((t) => t.label).join(', ')}. Os orcamentos feitos no modo sobem quando o sistema voltar e voce clicar em Voltar ao normal.`}
          style={BOTAO}
        >
          Modo Emergencia
        </button>
      </div>
    );
  }
  // Modo ligado e o servidor ja respondendo: so sai no clique ([DA-849]) — um soluco de segundos
  // nao tira a pessoa do modo no meio do atendimento. Se cair de novo, volta a tarja de baixo.
  if (!semConexao) {
    return (
      <div role="alert" style={{ ...COR, padding: '3px 16px', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap', overflow: 'hidden' }}>
        <LifeBuoy size={16} />
        <span>MODO EMERGENCIA — O SISTEMA VOLTOU.</span>
        <button type="button" onClick={voltarAoNormal} title="Sai do modo emergencia; os orcamentos feitos nele sobem para o sistema." style={BOTAO}>
          Voltar ao normal
        </button>
        <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>Os orcamentos da emergencia sobem ao clicar.</span>
      </div>
    );
  }
  return (
    // Uma linha so: cada pixel aqui e' uma linha de orcamento a menos. O detalhe vai no `title`.
    <div
      role="alert"
      title={`Funcionam: ${TELAS_EMERGENCIA.map((t) => t.label).join(', ')}. Os orcamentos feitos agora sobem quando o sistema voltar e voce clicar em Voltar ao normal.`}
      style={{ ...COR, padding: '4px 16px', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', overflow: 'hidden' }}
    >
      <LifeBuoy size={16} />
      <span>MODO EMERGENCIA — sistema fora do ar.</span>
      <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>Quando ele voltar, aparece o botao Voltar ao normal.</span>
    </div>
  );
}

/** O que aparece no lugar de uma tela que nao funciona sem o servidor. */
export function TelaIndisponivelNaEmergencia() {
  const navigate = useNavigate();
  return (
    <div style={{ padding: 32, maxWidth: 560 }}>
      <h2 className="pagina-titulo" style={{ marginBottom: 8 }}>Indisponivel no modo emergencia</h2>
      <p style={{ marginBottom: 16 }}>Esta tela precisa do sistema no ar. Enquanto ele esta fora, funcionam:</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {TELAS_EMERGENCIA.map((t) => (
          <button key={t.to} type="button" className="btn-primario" onClick={() => navigate(t.to)}>{t.label}</button>
        ))}
      </div>
    </div>
  );
}
